import { FC, memo, useCallback, useState } from 'react'
import { Product, ProductColor } from '@/network/graphql.g'
import { Box, Flex } from '@/components/atoms/Grid'
import { Capitalized } from '@/components/atoms/typography'
import useTranslation from 'next-translate/useTranslation'
import ProductDetailColor from '@/components/organisms/productDetailSideMenu/productDetailColor'
import ProductsSlider, { ProductSlide } from '../productsSlider'
import styled from 'styled-components'
import { ID } from 'graphql-ws'

import { ArrowsPosition } from '../productsSlider/productsSlider'

type ProductDetailColorsChooserProps = {
  colors: ProductColor[]
  productBrand: string
  mainCategoryId: ID
  currentImage: ID
  setHoverColorVariant: (hoverColorVariant: string) => void
  setHoverColorId?: (hoverColorId: string) => void
  numberOfSlides: number
  unavailableProduct?: Product
} & Rest

const ProductDetailColorsChooser: FC<ProductDetailColorsChooserProps> = ({
  colors,
  productBrand,
  mainCategoryId,
  currentImage,
  setHoverColorVariant,
  numberOfSlides,
  setHoverColorId,
  unavailableProduct,
  ...rest
}) => {
  const { t } = useTranslation('common')
  const [expand, setExpand] = useState<boolean>(false)
  const handleChange = () => {
    setExpand((expand) => !expand)
  }

  const sortImages = useCallback(
    () => [...colors].sort((a, b) => +a.id - +b.id),
    [colors]
  )

  const showArrows = sortImages().length > numberOfSlides

  return (
    <StyledBox {...rest} data-testid="productDetailColorsChooser">
      <Flex justifyContent="space-between">
        <Capitalized my="10px">
          {!unavailableProduct
            ? t('ProductDetail.changeColor')
            : t('ProductDetail.NotAvailable.colorVariantsTitle')}
        </Capitalized>
        {colors.length > numberOfSlides && (
          <StyledCapitalized my="10px" onClick={handleChange}>
            {!expand ? t('ProductDetail.showAll') : t('ProductDetail.hideAll')}
          </StyledCapitalized>
        )}
      </Flex>
      {!expand ? (
        <Box position="relative" pb={`${(1 / numberOfSlides) * 1.5 * 100}%`}>
          <ProductsSlider
            slidesToShow={numberOfSlides}
            showArrows={showArrows}
            position="absolute"
            style={{ left: 0, right: 0 }}
            slidesPerGroup={Math.floor(numberOfSlides)}
            fillArrowBackground={true}
            arrowsPosition={showArrows && ArrowsPosition.Inside}
          >
            {sortImages().map((color, offset) => (
              <ProductSlide key={offset}>
                <ProductDetailColor
                  color={color}
                  productBrand={productBrand}
                  mainCategoryId={mainCategoryId}
                  position={offset + 1}
                  setHoverColorVariant={setHoverColorVariant}
                  setHoverColorId={setHoverColorId}
                  currentImage={currentImage === color.id}
                  unavailableProduct={unavailableProduct}
                />
              </ProductSlide>
            ))}
          </ProductsSlider>
        </Box>
      ) : (
        <Flex flexWrap="wrap">
          {sortImages().map((color, offset) => (
            <Box key={offset} width={`${100 / Math.ceil(numberOfSlides)}%`}>
              <ProductDetailColor
                color={color}
                productBrand={productBrand}
                mainCategoryId={mainCategoryId}
                position={offset + 1}
                setHoverColorVariant={setHoverColorVariant}
                setHoverColorId={setHoverColorId}
                currentImage={currentImage === color.id}
                unavailableProduct={unavailableProduct}
              />
            </Box>
          ))}
        </Flex>
      )}
    </StyledBox>
  )
}

export default memo(ProductDetailColorsChooser)

const StyledBox = styled(Box)`
  text-align: left;
  margin-top: 12px;
  clear: both;
`

const StyledCapitalized = styled(Capitalized)`
  cursor: pointer;
  font-weight: normal;
`
